import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Send, Mail, Loader2, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { API, mondayOf, addDays, isoDate, fmtDay } from './_helpers';
import ShiftPill from './ShiftPill';
import NewShiftModal from './NewShiftModal';
import SitesModal from './SitesModal';
import SwapProposeModal from './SwapProposeModal';
import NotifyRosterModal from './NotifyRosterModal';

export default function WeekGrid({ isManager, meId }) {
  const [anchor, setAnchor] = useState(mondayOf(new Date()));
  const [shifts, setShifts] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState('all'); // 'all' | site_id
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(null);  // {date, user_id?}
  const [showSites, setShowSites] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [swapFrom, setSwapFrom] = useState(null);  // shift the staff is offering
  const [assignOpen, setAssignOpen] = useState(null); // open shift being assigned
  // Drag-drop reschedule state (iter 128). Holds the shift currently being
  // dragged so cells can decide whether to render the "drop here" highlight.
  const [dragShift, setDragShift] = useState(null);
  const [dropMsg, setDropMsg] = useState(null);  // transient toast string

  const days = useMemo(() => Array.from({length: 7}, (_, i) => isoDate(addDays(anchor, i))), [anchor]);

  const load = async () => {
    setLoading(true);
    try {
      const siteQ = siteFilter !== 'all' ? `&site_id=${siteFilter}` : '';
      const [s, sitesRes, u, os] = await Promise.all([
        axios.get(`${API}/roster/shifts?from=${days[0]}&to=${days[6]}${siteQ}`, { withCredentials: true }),
        axios.get(`${API}/roster/sites`, { withCredentials: true }).catch(() => ({ data: { sites: [] } })),
        isManager
          ? axios.get(`${API}/users?limit=200`, { withCredentials: true }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
        isManager
          ? axios.get(`${API}/roster-kronos/open-shifts?from=${days[0]}&to=${days[6]}`, { withCredentials: true }).catch(() => ({ data: { open_shifts: [] } }))
          : Promise.resolve({ data: { open_shifts: [] } }),
      ]);
      setShifts(s.data.shifts);
      setSites(sitesRes.data.sites || []);
      const users = Array.isArray(u.data) ? u.data : (u.data.users || []);
      setStaff(users.filter(x => x.active !== false).sort((a, b) => (a.name||'').localeCompare(b.name||'')));
      const openList = (os.data.open_shifts || []).filter(o =>
        siteFilter === 'all' || o.site_id === siteFilter
      );
      setOpenShifts(openList);
    } finally { setLoading(false); }
  };
  useEffect(() => { load();   }, [anchor.getTime(), siteFilter]);

  const siteById = useMemo(() => Object.fromEntries(sites.map(s => [s.site_id, s])), [sites]);

  // Iter 124b — per-site utilisation strip: published+draft shifts grouped by
  // site, counting unique staff + total hours across the visible week.
  const siteStats = useMemo(() => {
    const acc = {};
    shifts.forEach(sh => {
      const key = sh.site_id || '__unassigned__';
      if (!acc[key]) acc[key] = { hours: 0, staff: new Set(), shifts: 0 };
      acc[key].hours += sh.duration_hours || 0;
      acc[key].staff.add(sh.user_id);
      acc[key].shifts += 1;
    });
    // Stable order: sites in their list order first, then unassigned
    const ordered = sites.filter(s => acc[s.site_id]).map(s => ({
      key: s.site_id, site: s,
      hours: acc[s.site_id].hours,
      staff: acc[s.site_id].staff.size,
      shifts: acc[s.site_id].shifts,
    }));
    if (acc.__unassigned__) {
      ordered.push({
        key: '__unassigned__', site: null,
        hours: acc.__unassigned__.hours,
        staff: acc.__unassigned__.staff.size,
        shifts: acc.__unassigned__.shifts,
      });
    }
    return ordered;
  }, [shifts, sites]);

  const totalHours = siteStats.reduce((sum, s) => sum + s.hours, 0);

  // For staff: only their own row.  For manager: every staff member.
  const visibleStaff = useMemo(() => {
    if (!isManager) {
      const mine = shifts.find(s => s.user_id === meId);
      return mine ? [{ id: meId, name: mine.user_name, email: mine.user_email }]
                  : (meId ? [{ id: meId, name: 'You' }] : []);
    }
    return staff;
  }, [isManager, staff, shifts, meId]);

  const draftCount = shifts.filter(s => s.status === 'draft').length;

  const bulkPublish = async () => {
    if (!window.confirm(`Publish ${draftCount} draft shifts and email everyone?`)) return;
    setPublishing(true);
    try {
      const r = await axios.post(`${API}/roster/shifts/bulk-publish`,
        { from: days[0], to: days[6] }, { withCredentials: true });
      const stats = r.data.email_stats || {};
      const sent = stats.sent || 0;
      const failed = stats.failed || 0;
      const mocked = stats.mocked || 0;
      const skipped = stats.skipped || 0;
      let msg = `${r.data.published} shift(s) published.\n\n`;
      if (sent > 0)   msg += `✅ ${sent} email(s) delivered.\n`;
      if (mocked > 0) msg += `⚠ ${mocked} email(s) MOCKED (no RESEND_API_KEY set).\n`;
      if (skipped > 0) msg += `↷ ${skipped} skipped (staff without an email on file).\n`;
      if (failed > 0) {
        msg += `\n❌ ${failed} email(s) FAILED to send.\n`;
        const ex = (r.data.email_failure_examples || [])[0];
        if (ex) {
          msg += `\nReason from provider:\n"${ex}"\n`;
          if (/verify a domain|onboarding@resend.dev|testing emails/i.test(ex)) {
            msg += `\nFix: In Resend, verify your own sending domain (e.g. spuradiologyassistant.com), then update RESEND_FROM in backend/.env to use that domain.`;
          }
        }
      }
      alert(msg);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not publish');
    }
    setPublishing(false);
  };

  // ──────────── Drag-drop reschedule (iter 128) ────────────
  // Rules:
  //   • Manager-only — the ShiftPill itself is only draggable when isManager.
  //   • Drop target must belong to the *same staff member* (per user spec).
  //   • Dropping onto the same day is a no-op.
  //   • On success: PATCH the date. Published shifts trigger the "moved"
  //     notification email server-side automatically.
  const handleCellDragOver = (e, cellUserId) => {
    if (!dragShift) return;
    if (dragShift.user_id !== cellUserId) return;  // disallow cross-row
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleCellDrop = async (e, cellUserId, cellDate) => {
    e.preventDefault();
    const ds = dragShift;
    setDragShift(null);
    if (!ds || ds.user_id !== cellUserId) return;
    if (ds.date === cellDate) return;
    try {
      await axios.patch(`${API}/roster/shifts/${ds.shift_id}`,
        { date: cellDate }, { withCredentials: true });
      const verb = ds.status === 'published' ? 'rescheduled and emailed' : 'moved';
      setDropMsg(`Shift ${verb}.`);
      setTimeout(() => setDropMsg(null), 3000);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not move shift');
    }
  };

  return (
    <div>
      {/* Top toolbar */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1">
          <button onClick={() => setAnchor(addDays(anchor, -7))} className="p-1.5 rounded border hover:bg-muted"
                  data-testid="roster-prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setAnchor(mondayOf(new Date()))} className="text-xs font-semibold px-3 py-1.5 rounded border hover:bg-muted"
                  data-testid="roster-today">Today</button>
          <button onClick={() => setAnchor(addDays(anchor, 7))} className="p-1.5 rounded border hover:bg-muted"
                  data-testid="roster-next-week"><ChevronRight className="w-4 h-4" /></button>
          <div className="ml-3 text-sm font-semibold" style={{ color: '#0d2644' }}>
            {fmtDay(days[0])} → {fmtDay(days[6])}
          </div>
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSites(true)} data-testid="roster-manage-sites"
              className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hover:bg-muted/40">
              <MapPin className="w-3.5 h-3.5" /> Sites
            </button>
            <button onClick={() => setShowNotify(true)} data-testid="roster-notify-staff"
              title="Send a 'log into portal' digest email to each staff member rostered this week"
              className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border hover:bg-muted/40">
              <Mail className="w-3.5 h-3.5" /> Notify staff
            </button>
            {draftCount > 0 && (
              <button onClick={bulkPublish} disabled={publishing} data-testid="roster-bulk-publish"
                className="text-sm font-semibold inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#15803d] text-white disabled:opacity-50">
                <Send className="w-4 h-4" /> Publish {draftCount} draft{draftCount===1?'':'s'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Site filter pills */}
      {sites.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <button onClick={() => setSiteFilter('all')} data-testid="site-filter-all"
            className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${siteFilter==='all' ? 'bg-[#0d2644] text-white border-[#0d2644]' : 'bg-card hover:bg-muted/40'}`}>
            All sites
          </button>
          {sites.filter(s => s.active).map(s => (
            <button key={s.site_id} onClick={() => setSiteFilter(s.site_id)} data-testid={`site-filter-${s.site_id}`}
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 ${siteFilter===s.site_id ? 'text-white border-transparent' : 'bg-card hover:bg-muted/40'}`}
              style={siteFilter===s.site_id ? { background: s.color } : {}}>
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Site utilisation strip — glanceable totals per site this week */}
      {siteStats.length > 0 && !loading && (
        <div className="flex items-center gap-2 mb-3 flex-wrap text-xs" data-testid="site-utilisation-strip">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">This week</span>
          {siteStats.map(s => {
            const color = s.site ? s.site.color : '#94a3b8';
            const label = s.site ? (s.site.short_code || s.site.name) : 'Unassigned';
            return (
              <span key={s.key} data-testid={`site-stat-${s.key}`}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border bg-card"
                style={{ borderColor: `${color}33` }}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                <span className="font-bold" style={{ color }}>{label}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-foreground"><strong>{s.staff}</strong> staff</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-foreground"><strong>{Math.round(s.hours * 10) / 10}h</strong></span>
              </span>
            );
          })}
          {siteStats.length > 1 && (
            <span className="text-muted-foreground ml-1">Total <strong className="text-foreground">{Math.round(totalHours * 10) / 10}h</strong></span>
          )}
        </div>
      )}

      {/* Grid */}
      <div className="border rounded-xl overflow-hidden bg-card">
        <div className="grid" style={{ gridTemplateColumns: `180px repeat(7, 1fr)` }}>
          <div className="bg-muted/40 px-3 py-2 text-xs font-bold uppercase tracking-wider border-b" style={{ color: '#0d2644' }}>Staff</div>
          {days.map(d => {
            const isToday = d === isoDate(new Date());
            return (
              <div key={d} className={`px-3 py-2 text-xs border-b border-l ${isToday ? 'bg-teal-50' : 'bg-muted/40'}`}>
                <div className="font-bold uppercase tracking-wider" style={{ color: '#0d2644' }}>
                  {new Date(d+'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short' })}
                </div>
                <div className="text-muted-foreground text-[10px]">
                  {new Date(d+'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="col-span-8 p-12 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            </div>
          )}
          {!loading && visibleStaff.length === 0 && (
            <div className="col-span-8 p-12 text-center text-muted-foreground">
              {isManager ? 'No staff yet.' : 'You don\'t have any shifts in this week.'}
            </div>
          )}
          {!loading && isManager && openShifts.length > 0 && (
            <React.Fragment>
              <div className="px-3 py-3 border-b text-sm font-semibold border-r bg-amber-50" data-testid="open-shifts-row-label">
                <div style={{ color: '#92400e' }}>Open shifts</div>
                <div className="text-[10px] text-amber-700 font-normal uppercase tracking-wider">Click to assign</div>
              </div>
              {days.map(d => {
                const cellOpens = openShifts.filter(o => o.date === d);
                return (
                  <div key={`open-${d}`} className="px-1.5 py-1.5 border-b border-l min-h-[64px] bg-amber-50/40 space-y-1"
                       data-testid={`open-shift-cell-${d}`}>
                    {cellOpens.map(o => (
                      <button key={o.open_shift_id} onClick={() => setAssignOpen(o)}
                        data-testid={`open-shift-${o.open_shift_id}`}
                        className="w-full text-left text-[11px] font-semibold px-1.5 py-1 rounded border border-amber-300 bg-amber-100 hover:bg-amber-200 text-amber-900 truncate">
                        {o.start}–{o.end}{o.role_label ? ` · ${o.role_label}` : ''}
                        <div className="text-[10px] font-normal text-amber-700 truncate">{o.site_label || o.site_id}</div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          )}
          {!loading && visibleStaff.map(s => (
            <React.Fragment key={s.id || s.email}>
              <div className="px-3 py-3 border-b text-sm font-semibold border-r" data-testid={`roster-row-${s.id}`}>
                <div style={{ color: '#0d2644' }}>{s.name || s.email}</div>
                <div className="text-[10px] text-muted-foreground font-normal uppercase tracking-wider">{s.role || ''}</div>
              </div>
              {days.map(d => {
                const cellShifts = shifts.filter(sh => sh.user_id === s.id && sh.date === d);
                const isValidDrop = dragShift && dragShift.user_id === s.id && dragShift.date !== d;
                const isInvalidDrop = dragShift && dragShift.user_id !== s.id;
                return (
                  <div key={d+s.id}
                       onDragOver={(e) => handleCellDragOver(e, s.id)}
                       onDrop={(e) => handleCellDrop(e, s.id, d)}
                       className={`px-1.5 py-1.5 border-b border-l min-h-[64px] transition group flex flex-col
                                   ${isValidDrop ? 'bg-teal-100/60 ring-2 ring-teal-400 ring-inset' :
                                     isInvalidDrop ? 'bg-red-50/40 opacity-50' :
                                     'hover:bg-muted/20'}`}
                       data-testid={`roster-cell-${s.id}-${d}`}>
                    {cellShifts.map(sh => <ShiftPill key={sh.shift_id} shift={sh} isManager={isManager} meId={meId}
                                                     site={sh.site_id ? siteById[sh.site_id] : null}
                                                     onChange={load}
                                                     onPropose={() => setSwapFrom(sh)}
                                                     onDragStartShift={setDragShift}
                                                     onDragEndShift={() => setDragShift(null)} />)}
                    {isManager && (
                      <button onClick={() => setShowNew({ date: d, user_id: s.id })}
                        title="Add shift" data-testid={`roster-add-${s.id}-${d}`}
                        className="opacity-0 group-hover:opacity-100 mt-auto pt-1 text-[10px] font-semibold text-[#0d2644]/70 hover:text-[#0d2644] inline-flex items-center justify-center gap-1 border border-dashed border-transparent group-hover:border-[#0d2644]/30 rounded transition-colors py-0.5">
                        <Plus className="w-3 h-3" /> Add shift
                      </button>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="text-xs text-muted-foreground mt-3 flex items-center gap-4 flex-wrap">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-slate-300"/> Draft</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-400"/> Awaiting accept</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-green-500"/> Accepted</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-500"/> Declined</span>
        {isManager && (
          <span className="ml-auto text-[11px] italic">
            Tip: drag a shift to another day in the same row to reschedule. Published moves email the staff member.
          </span>
        )}
      </div>

      {/* Drop confirmation toast */}
      {dropMsg && (
        <div data-testid="roster-drop-toast"
             className="fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-lg bg-[#0d2644] text-white text-sm font-semibold shadow-lg">
          {dropMsg}
        </div>
      )}

      {showNew && (
        <NewShiftModal initial={showNew} staff={staff} sites={sites}
          onClose={() => setShowNew(null)}
          onSaved={() => { setShowNew(null); load(); }} />
      )}
      {showSites && (
        <SitesModal sites={sites} onClose={() => setShowSites(false)} onChanged={load} />
      )}
      {showNotify && (
        <NotifyRosterModal
          rangeFrom={days[0]} rangeTo={days[6]}
          rangeLabel={`${fmtDay(days[0])} → ${fmtDay(days[6])}`}
          onClose={() => setShowNotify(false)} />
      )}
      {swapFrom && (
        <SwapProposeModal myShift={swapFrom} allShifts={shifts} sites={sites}
          onClose={() => setSwapFrom(null)} onCreated={() => { setSwapFrom(null); load(); }} />
      )}
      {assignOpen && (
        <AssignOpenShiftModal open={assignOpen} staff={staff}
          onClose={() => setAssignOpen(null)}
          onAssigned={() => { setAssignOpen(null); load(); }} />
      )}
    </div>
  );
}

function AssignOpenShiftModal({ open, staff, onClose, onAssigned }) {
  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const assign = async () => {
    if (!userId) { setErr('Pick a staff member.'); return; }
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/roster-kronos/open-shifts/${open.open_shift_id}/assign`,
        { user_id: userId }, { withCredentials: true });
      onAssigned();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to assign.');
    }
    setBusy(false);
  };
  const del = async () => {
    if (!window.confirm('Delete this open shift?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/roster-kronos/open-shifts/${open.open_shift_id}`, { withCredentials: true });
      onAssigned();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to delete.');
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="assign-open-shift-modal">
        <h3 className="text-lg font-semibold" style={{ color: '#0d2644' }}>Assign open shift</h3>
        <div className="text-sm text-muted-foreground">
          <div><strong>{open.date}</strong> · {open.start}–{open.end}</div>
          <div>{open.site_label || open.site_id}{open.role_label ? ` · ${open.role_label}` : ''}</div>
        </div>
        <label className="block text-sm">
          <span className="text-xs font-medium text-muted-foreground">Staff member</span>
          <select value={userId} onChange={e => setUserId(e.target.value)} data-testid="assign-user-select"
            className="w-full mt-0.5 px-3 py-2 rounded-lg border bg-background text-sm">
            <option value="">— pick —</option>
            {staff.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </label>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex justify-between items-center gap-2 pt-2">
          <button onClick={del} disabled={busy} data-testid="delete-open-shift"
            className="text-xs text-red-600 hover:underline">Delete open shift</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border">Cancel</button>
            <button onClick={assign} disabled={busy} data-testid="assign-confirm"
              className="text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-40"
              style={{ background: '#7C3AED' }}>{busy ? 'Assigning…' : 'Assign'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
